import { Today } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
} from '@mui/material';
import { useNotify, useRefresh } from 'react-admin';
import { useEffect, useState } from 'react';
import { goApi } from '../../services';
import { LoadingSection } from '../LoadingSection';
import { addDays } from 'date-fns';
import { ProductCard } from './ProductCard';
import UsersPhoneAutocomplete from '../inputs/autoCompleteInputs/UsersPhoneAutocomplete';
import { apiRoutes } from '../../services/apiRoutes';

const AssignEcomProduct = ({ type, typeId, onClose, onValidityChange, onSuccess }: any) => {
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<any[]>([]);
    const [studentId, setStudentId] = useState<string | number | null>(null);
    const [productId, setProductId] = useState<string | number | null>(null);
    const [endsOn, setEndsOn] = useState<string>('');
    const [offerAmount, setOfferAmount] = useState<number | ''>('');
    const notify = useNotify();
    const refresh = useRefresh();

    const fetchProducts = async () => {
        try {
            const resp: any = await goApi.get(apiRoutes.GET_ECOM_PRODUCTS(type, typeId));
            const items = Array.isArray(resp?.data)
                ? resp.data
                : resp?.data?.items ?? (Array.isArray(resp) ? resp : []);
            const normalized = (items || []).map((p: any) => ({
                ...p,
                validity_in_days: p.validityInDays ?? p.validity_in_days,
                delivery_amount: p.deliveryAmount ?? p.delivery_amount,
                title: p.title ?? p.name,
            }));
            setProducts(normalized);
        } catch (e) {
            console.error('Failed to load products', e);
            setProducts([]);
        }
    };

    if (!type && !typeId) {
        return <LoadingSection />;
    }

    const selectedProduct = products.find((p: any) => p.id === productId);

    const computeDefaultEndDate = () => {
        if (!selectedProduct) return '';
        const d = addDays(new Date(), selectedProduct?.validity_in_days);
        const iso = new Date(d).toISOString().slice(0, 10);
        return iso;
    };

    const computeDefaultOffer = () => {
        if (!selectedProduct) return '';
        return (
            (selectedProduct.amount || 0) -
            (selectedProduct.discount || 0) +
            (selectedProduct.delivery_amount || 0)
        );
    };

    useEffect(() => {
        if (type && typeId) {
            fetchProducts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, typeId]);

    const onChangeProduct = (value: any) => {
        setProductId(value);
        const product = products.find((p: any) => p.id === value);
        if (product) {
            const d = addDays(new Date(), product?.validity_in_days);
            const iso = new Date(d).toISOString().slice(0, 10);
            setEndsOn(iso);
            const offer =
                (product.amount || 0) - (product.discount || 0) + (product.delivery_amount || 0);
            setOfferAmount(offer);
        }
        if (onValidityChange) onValidityChange(Boolean(studentId && value));
    };

    const postSave = async (e: any) => {
        e.preventDefault();
        if (loading) return;
        if (!studentId || !productId || !endsOn || offerAmount === '' || offerAmount == null) {
            return;
        }
        setLoading(true);
        const payload: any = {
            student_id: studentId,
            product_id: productId,
            ends_on: endsOn,
            offer_amount: parseInt(String(offerAmount)),
            type,
            typeId,
        };
        try {
            await goApi.post(apiRoutes.ECOM_ORDERS, payload);
            refresh();
            notify('Order assigned successfully', { type: 'info' });
            if (onSuccess) onSuccess();
        } catch (err) {
            notify('Failed to assign order', { type: 'warning' });
        } finally {
            setLoading(false);
            onClose();
        }
    };

    return (
        <div style={{ width: '500px', maxWidth: '90vw' }}>
            <form id="assign-ecom-product-form" onSubmit={postSave}>
                <div className={'pt-1'}>
                    <UsersPhoneAutocomplete
                        label={'Student by phone'}
                        role={'STUDENT'}
                        fullWidth
                        onChange={id => {
                            setStudentId(id);
                            if (onValidityChange) onValidityChange(Boolean(id && productId));
                        }}
                    />
                </div>

                <div className={'pt-3'}>
                    <Select
                        fullWidth
                        value={productId ?? ''}
                        displayEmpty
                        required
                        onChange={(e: any) => onChangeProduct(e.target.value)}
                    >
                        <MenuItem value={''} disabled>
                            Select Plan
                        </MenuItem>
                        {products.map((p: any) => (
                            <MenuItem key={p.id} value={p.id}>
                                {p.name || `Product ${p.id}`}
                            </MenuItem>
                        ))}
                    </Select>
                </div>

                {selectedProduct && (
                    <div className={'w-full pt-3'}>
                        <TextField
                            label={'Ends On'}
                            type={'date'}
                            fullWidth
                            value={endsOn || computeDefaultEndDate()}
                            onChange={e => setEndsOn(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <ProductCard compactView={true} product={selectedProduct} />

                        <div className={'pt-2'}>
                            <TextField
                                label={'Offer Amount'}
                                fullWidth
                                type={'number'}
                                required
                                value={offerAmount}
                                onChange={e => {
                                    const val = e.target.value;
                                    setOfferAmount(val === '' ? '' : Number(val));
                                }}
                            />
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

const AssignEcomProductModal = ({ title, type, typeId, onSuccess }: any) => {
    const [openStudentModal, setOpenStudentModal] = useState(false);
    const [canSave, setCanSave] = useState(false);
    return (
        <>
            <Button
                onClick={() => {
                    setOpenStudentModal(true);
                }}
                startIcon={<Today />}
            >
                {title || 'Assign Ecom Product'}
            </Button>

            <Dialog
                open={openStudentModal}
                onClose={() => {
                    setOpenStudentModal(false);
                }}
            >
                <DialogTitle>{title || 'Assign Ecom Product'}</DialogTitle>
                <DialogContent dividers>
                    <AssignEcomProduct
                        type={type}
                        typeId={typeId}
                        onClose={() => setOpenStudentModal(false)}
                        onValidityChange={(valid: boolean) => setCanSave(valid)}
                        onSuccess={onSuccess}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenStudentModal(false)}>Cancel</Button>
                    <Button
                        type="submit"
                        form="assign-ecom-product-form"
                        variant="contained"
                        disabled={!canSave}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
export default AssignEcomProductModal;
